<template>
  <div class="record-management-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:clock" width="24" height="24" />
        图文发布记录
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.title" placeholder="搜索图文标题" clearable size="large" class="search-input"
            @keyup.enter="handleSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-select v-model="searchForm.gzh_id" placeholder="选择账号" clearable size="large"
            style="width: 180px; margin-right: 15px">
            <el-option label="不选择" :value="null" />
            <el-option v-for="item in gzhOptions" :key="item.id" :label="item.ghz_name" :value="item.id" />
          </el-select>

          <el-button type="primary" size="large" @click="handleSearch">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            搜索
          </el-button>

          <el-button type="info" size="large" @click="resetSearch" style="margin-left: 10px">
            <Icon icon="heroicons:arrow-path" width="18" height="18" />
            重置
          </el-button>
        </div>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="recordList" stripe v-loading="loading" class="record-table"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
          <el-table-column label="账号" prop="gzh_name" width="180" />
          <el-table-column label="账号类型" width="120">
            <template #default="{ row }">
              <el-tag :type="getPlatformTagType(row.field_type)" size="small">
                {{ getPlatformText(row.field_type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="图文标题" min-width="220" show-overflow-tooltip>
            <template #default="{ row }">
              <span :class="{ 'empty-title': !row.work_description && !row.title && !row.topic }">
                {{ row.work_description || row.title || row.topic || '无标题' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="任务名称" prop="draft_task_name" width="150" />

          <el-table-column label="错误提示" width="180">
            <template #default="{ row }">
              <el-popover v-if="row.error_message" placement="top-start" width="300" trigger="hover" :teleported="true">
                <template #reference>
                  <el-tag type="danger" size="small" style="cursor: pointer">
                    <Icon icon="heroicons:exclamation-triangle" width="14" height="14" />
                    查看错误
                  </el-tag>
                </template>
                <div class="error-message-content">
                  {{ row.error_message }}
                </div>
              </el-popover>
              <span v-else class="empty-value">无错误</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120">
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.status)" size="small">
                {{ getStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="草稿时间" prop="drafted_at" width="180" />
          <el-table-column label="发布时间" prop="published_at" width="180" />
          <el-table-column label="备注" prop="notes" width="180" show-overflow-tooltip />

          <el-table-column label="重试" width="100" fixed="right">
            <template #default="{ row }">
              <el-button type="warning" size="small" :disabled="[1, 2, 4, 5].includes(row.status)"
                @click="handleRetry(row)">
                <Icon icon="heroicons:arrow-path" width="14" height="14" />
                重试
              </el-button>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="120" fixed="right">
            <template #default="scope">
              <el-button type="danger" size="small" @click="handleDelete(scope.row.id)"
                :disabled="scope.row.status === 2">
                <Icon icon="heroicons:trash" width="14" height="14" />
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout"
          @size-change="fetchRecordList" @current-change="fetchRecordList" background />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'XiaohongshuRecord',
  components: { Icon },
  data() {
    return {
      loading: false,
      searchForm: {
        title: '',
        category_id: null,
        gzh_id: null
      },
      recordList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      categoryOptions: [],
      gzhOptions: [],
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    }
  },
  async mounted() {
    await this.fetchGzhOptions();
    await this.fetchCategoryOptions();
    await this.fetchRecordList();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
    getGzhFieldType(gzhId) {
      const gzh = this.gzhOptions.find(g => g.id == gzhId);
      return gzh ? gzh.field_type : null;
    },
    getPlatformText(type) {
      const platformMap = {
        2: '某音',
        3: '某手',
        4: '蝴蝶号',
        5: '小红薯',
        6: '小B站',
        7: '海外某音'
      };
      return platformMap[type] || `暂无`;
    },
    getPlatformTagType(type) {
      const typeMap = {
        2: 'danger',
        3: 'primary',
        4: 'success',
        5: 'warning',
        6: 'info',
        7: ''
      };
      return typeMap[type] || 'info';
    },
    async handleRetry(row) {
      const disallowedStatuses = [1, 2, 4, 5];
      if (disallowedStatuses.includes(row.status)) {
        ElMessage.warning(`当前状态为"${this.getStatusText(row.status)}"，不能重试`);
        return;
      }
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/ks_tw_record_management.php`,
          {
            action: 'retry',
            id: row.id,
            store_id: this.userInfo.id
          }
        );
        if (data.success) {
          ElMessage.success('任务已重置为待发送状态');
          this.fetchRecordList();
        } else {
          ElMessage.error(data.error || '重试任务失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '重试任务失败';
        ElMessage.error(`重试失败: ${errorMsg}`);
        console.error('重试错误:', error);
      }
    },
    async fetchCategoryOptions() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/library_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );
        if (data.data) {
          this.categoryOptions = data.data;
        }
      } catch (error) {
        console.error('获取图文分类失败:', error);
      }
    },
    async fetchGzhOptions() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );
        if (data.data) {
          this.gzhOptions = data.data;
        }
      } catch (error) {
        console.error('获取账号列表失败:', error);
      }
    },
    async fetchRecordList() {
      this.loading = true;
      try {
        const params = {
          action: 'search',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };
        if (this.searchForm.title) {
          params.title = this.searchForm.title;
        }
        if (this.searchForm.category_id) {
          params.article_category_id = this.searchForm.category_id;
        }
        if (this.searchForm.gzh_id) {
          params.gzh_id = this.searchForm.gzh_id;
        }
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/ks_tw_record_management.php`,
          params
        );
        if (data.data) {
          this.recordList = data.data.map(item => ({
            ...item,
            category_name: this.getCategoryName(item.article_category_id),
            gzh_name: this.getGzhName(item.gzh_id),
            field_type: this.getGzhFieldType(item.gzh_id)
          }));
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        ElMessage.error('获取记录失败');
        console.error('获取记录错误:', error);
      } finally {
        this.loading = false;
      }
    },
    getCategoryName(id) {
      const category = this.categoryOptions.find(cat => cat.id == id);
      return category ? category.article_category : `分类${id}`;
    },
    getGzhName(id) {
      const gzh = this.gzhOptions.find(g => g.id == id);
      return gzh ? gzh.ghz_name : `账号${id}`;
    },
    getStatusText(status) {
      const statusMap = {
        1: '待发送',
        2: '发送成功',
        3: '发送失败',
        4: '发布处理中',
        5: '已提交发布'
      };
      return statusMap[status] || `状态${status}`;
    },
    getStatusTagType(status) {
      const typeMap = {
        1: 'warning',
        2: 'success',
        3: 'danger',
        4: 'info',
        5: 'primary'
      };
      return typeMap[status] || 'info';
    },
    handleSearch() {
      this.pagination.page = 1;
      this.fetchRecordList();
    },
    resetSearch() {
      this.searchForm = {
        title: '',
        category_id: null,
        gzh_id: null
      };
      this.pagination.page = 1;
      this.fetchRecordList();
    },
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除此记录吗？', '警告', {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        });
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/ks_tw_record_management.php`,
          {
            action: 'delete',
            id: id,
            store_id: this.userInfo.id
          }
        );
        if (data.success) {
          ElMessage.success('删除成功');
          this.fetchRecordList();
        } else {
          ElMessage.error(data.error || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          ElMessage.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.record-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    width: 300px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left {
      width: 100%;
      flex-direction: column;
    }

    .search-input {
      width: 100% !important;
    }

    .el-select {
      width: 100% !important;
      margin-right: 0 !important;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;

  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.empty-title {
  color: #7a7a9a;
  font-style: italic;
}

.empty-value {
  color: #7a7a9a;
  font-style: italic;
  font-size: 12px;
}

.error-message-content {
  max-height: 200px;
  overflow-y: auto;
  word-break: break-word;
  white-space: pre-wrap;
  padding: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #e0e0e0;
  background: rgba(18, 14, 33, 0.98);
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &.is-focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  .el-input__inner {
    color: #fff;
    background: transparent;

    &::placeholder {
      color: #5a5a7a;
    }
  }
}

:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}
</style>

<!-- 非 scoped 全局样式 -->
<style lang="scss">
.el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
}
</style>